/**
 * Wording and small helpers for the admin's "External Approvals" list: weekly
 * sheets a tradie sent to a supervisor outside the company.
 */

// What the API calls each state, and what the admin reads.
export const STATUS = {
  awaiting_external_approval: {
    label: 'Awaiting External Approval',
    tone: 'wait',
  },
  approved: { label: 'Approved', tone: 'ok' },
  approved_with_changes: { label: 'Approved with Changes', tone: 'ok' },
  approved_by_admin: { label: 'Approved by Company Admin', tone: 'ok' },
  rejected: { label: 'Rejected - Amendment Required', tone: 'bad' },
  finalised: { label: 'Finalised', tone: 'done' },
}

// Approved, but the hours have not been released to payroll yet.
export const FINALISABLE = [
  'approved',
  'approved_with_changes',
  'approved_by_admin',
]

export const isFinalisable = (status) => FINALISABLE.includes(status)

export function statusMeta(status) {
  return STATUS[status] || { label: status || 'Unknown', tone: 'wait' }
}

// 450 -> "7h 30m", 480 -> "8h", 0 -> "0h"
export function formatMinutes(total) {
  const minutes = Math.max(0, Math.round(Number(total) || 0))
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m ? `${h}h ${m}m` : `${h}h`
}

// "2026-10-19" and "2026-10-25" -> "19 Oct – 25 Oct"
export function weekLabel(start, end) {
  const fmt = (iso) => {
    const d = new Date(`${iso}T00:00:00`)
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
  }
  return `${fmt(start)} – ${fmt(end)}`
}

// "07:00:00" -> "07:00"
export const hm = (time) => (time ? String(time).slice(0, 5) : '')

// The filter pills, in the order an admin works through them. `match` decides
// which rows each one shows; "ready" is the daily job (finalise what is approved).
export const FILTERS = [
  {
    key: 'ready',
    label: 'Ready to finalise',
    match: (r) => isFinalisable(r.status),
  },
  {
    key: 'awaiting',
    label: 'Awaiting supervisor',
    match: (r) => r.status === 'awaiting_external_approval',
  },
  {
    key: 'overdue',
    label: 'Supervisor overdue',
    match: (r) => r.overdue === true,
  },
  // Weeks a tradie worked and never sent: a different list (nothing to approve
  // yet), so no row of the main list matches it.
  { key: 'unsent', label: 'Not sent by tradie', match: () => false },
  {
    key: 'rejected',
    label: 'Rejected',
    match: (r) => r.status === 'rejected',
  },
  {
    key: 'finalised',
    label: 'Finalised',
    match: (r) => r.status === 'finalised',
  },
  { key: 'all', label: 'All', match: () => true },
]

// What to do with a validation error from the API (422 body).
export function firstError(err, fallback) {
  const errors = err && err.data && err.data.errors
  if (errors) {
    const first = Object.values(errors)[0]
    if (first && first[0]) return first[0]
  }
  return (err && err.data && err.data.message) || fallback
}
